import { Logo } from "@storytell/core";
import { FaSolidGraduationCap } from "solid-icons/fa";
import { RiUserFacesUserStarLine } from "solid-icons/ri";
import {
  TbActivity,
  TbArrowLeft,
  TbArrowRight,
  TbArrowsDiagonal,
  TbBell,
  TbBolt,
  TbBrain,
  TbCalendarEvent,
  TbCheck,
  TbChevronDown,
  TbChevronsRight,
  TbCube,
  TbDeviceDesktop,
  TbFilter,
  TbGift,
  TbGlobe,
  TbHelp,
  TbInfoCircle,
  TbLayoutGrid,
  TbLayoutList,
  TbLayoutSidebar,
  TbListCheck,
  TbLoader2,
  TbMail,
  TbPencil,
  TbPlayerPause,
  TbPlayerPlay,
  TbPlus,
  TbPuzzle,
  TbSearch,
  TbSettings,
  TbTrash,
  TbWaveSquare,
} from "solid-icons/tb";
import { type Component, For, Show } from "solid-js";
import { Dynamic } from "solid-js/web";
import { LuLibrary } from "~/components/icons/LuLibrary";
import { assetLifecycleStatusPillClasses } from "~/components/utils/lifecycleStateMappers";
import { useI18n } from "~/i18n";
import { INTEGRATION_PROVIDER_ICONS } from "~/lib/integrations/integrationProviderIcons";
import { formatCredits } from "~/utils/creditFormat";
import { formatCronSchedule, formatTriggerKind } from "~/utils/cronDisplay";
import type { ActionsData } from "../ActionsMockup";
import { at, Cursor } from "./parts";

type Action = ActionsData["actions"][number];
type Run = Action["runs"][number];

/**
 * When each beat lands, in seconds on screen: Run Now is clicked, the run opens
 * a row in the history untitled and running, picks up its title, finishes, and
 * is hovered to show what it produced; the action's card catches up last.
 */
const T = { cursor: 0.6, press: 1.65, landed: 1.95, titled: 3.4, hover: 4.2, expand: 5.25, card: 5.6 };

/** The web app's nav row: a borderless button that fills when its page is open. */
const NAV_ROW = "flex w-full items-center justify-start gap-1.5 rounded-lg py-2 pr-1.5 text-sm whitespace-nowrap";

/** A borderless-fill button in the shared kit: hairline border, text set by the caller. */
const BUTTON = "flex items-center justify-center gap-1.5 rounded-lg border border-muted-border p-1.5";

const relative = (hours: number, lang: string) => {
  const format = new Intl.RelativeTimeFormat(lang, { numeric: "auto" });
  if (hours < 1) return format.format(0, "second");
  if (hours < 24) return format.format(-hours, "hour");
  if (hours < 24 * 7) return format.format(-Math.round(hours / 24), "day");
  return format.format(-Math.round(hours / (24 * 7)), "week");
};

const clockTime = (now: string, hoursAgo: number, lang: string) =>
  new Date(Date.parse(`${now}Z`) - hoursAgo * 3_600_000).toLocaleTimeString(lang, {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "UTC",
  });

/** The web app's left navigation, expanded, inside a project with Actions open. */
export const WebSidebar: Component<{ data: ActionsData }> = (props) => {
  const { t, currentLanguage } = useI18n();
  const link = (icon: Component<{ class?: string }>, label: string, active?: boolean) => (
    <span class={`${NAV_ROW} pl-4`} classList={{ "bg-panel-hover font-medium text-primary-text": active }}>
      <Dynamic component={icon} class="size-4 shrink-0" aria-hidden="true" />
      {label}
    </span>
  );

  return (
    <div class="flex h-full w-50 shrink-0 flex-col space-y-2 border-e border-primary-border bg-panel p-2">
      <div class="flex h-9 items-center justify-between pr-2.5">
        <span class="px-4">
          <Logo class="h-5 w-auto" markClass="text-accent" textClass="text-primary-text" />
        </span>
        <span class="flex items-center">
          <TbSearch class="m-1.5 size-4" aria-hidden="true" />
          <TbLayoutSidebar class="m-1.5 size-4" aria-hidden="true" />
        </span>
      </div>

      <div class="mt-1 pb-1">
        <span class="flex min-h-9 w-full items-center gap-1.5 rounded-lg px-4 py-1.5">
          <TbCube class="size-4 shrink-0 text-muted-text" aria-hidden="true" />
          <span class="min-w-0 flex-1 truncate text-sm leading-4 font-medium text-primary-text">
            {props.data.project}
          </span>
          <TbChevronDown class="size-3.5 shrink-0 text-muted-text" aria-hidden="true" />
        </span>
      </div>

      <div class="border-t border-primary-border pb-1" />

      <span class={`${NAV_ROW} min-h-8 bg-brand pl-4 text-on-brand`}>
        <TbPlus class="size-4 shrink-0" aria-hidden="true" />
        {t("nav_new")}
      </span>

      <div class="flex flex-col pb-1">
        {link(TbListCheck, t("nav_chats"))}
        <span class="relative">
          {link(LuLibrary, t("project_homepage.library"))}
          <TbChevronDown
            class="absolute top-1/2 right-3.5 size-3.5 -translate-y-1/2 text-muted-text"
            aria-hidden="true"
          />
        </span>
        {link(FaSolidGraduationCap, t("skills.title"))}
        {link(RiUserFacesUserStarLine, t("personas.page_title"))}
        {link(TbBolt, t("actions.title"), true)}
        {link(TbWaveSquare, t("nav_live_sessions"))}
      </div>

      <div class="border-t border-primary-border pb-1" />

      <div class="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div class="mb-1 flex items-center justify-between gap-2">
          <span class="px-4 text-xs font-semibold tracking-wide text-secondary-text uppercase">
            {t("nav_recent_chats")}
          </span>
          <TbFilter class="m-1 size-4 text-muted-text" aria-hidden="true" />
        </div>
        <span class="px-4 pt-2 pb-0.5 text-xs font-medium text-muted-text">{t("nav_recent_today")}</span>
        <For each={props.data.tasks}>
          {(task) => (
            <span class="flex w-full items-center gap-2 rounded-md px-4 py-1.5 text-sm">
              <span class="min-w-0 flex-1 truncate">{task.title}</span>
              <span class="shrink-0 text-xs text-muted-text tabular-nums">
                {clockTime(props.data.now, task.hoursAgo, currentLanguage())}
              </span>
            </span>
          )}
        </For>
      </div>

      <div class="shrink-0">
        <div class="mb-2 flex items-center justify-center gap-1 border-t border-primary-border pt-2">
          <For each={[TbPuzzle, TbGift, TbHelp, TbDeviceDesktop, TbBell]}>
            {(icon) => <Dynamic component={icon} class="m-2 size-5 text-secondary-text" aria-hidden="true" />}
          </For>
        </div>
        <span class="flex items-center gap-3 rounded-lg px-4 py-2">
          <span
            class="grid size-8 shrink-0 place-items-center rounded-full text-sm font-medium text-white"
            style={{ background: props.data.user.color }}
          >
            {props.data.user.name[0]}
          </span>
          <span class="min-w-0 flex-1 truncate text-sm font-medium text-primary-text">{props.data.user.name}</span>
          <TbSettings class="size-5 shrink-0" aria-hidden="true" />
        </span>
      </div>
    </div>
  );
};

/** The project's Actions page in its default card grid, newest first. */
export const ActionsList: Component<{ data: ActionsData }> = (props) => {
  const { t } = useI18n();

  return (
    <div class="flex h-full min-w-0 flex-col">
      <div class="p-4 pb-0">
        <div class="mb-4 flex min-w-0 items-center gap-2">
          <h3 class="flex min-w-0 shrink items-center gap-2 text-xl font-semibold text-primary-text">
            <TbBolt class="size-6 shrink-0 text-brand" aria-hidden="true" />
            <span class="truncate">{t("actions.title")}</span>
          </h3>
          <span class="shrink-0 rounded-lg bg-brand px-3 py-1.5 text-sm text-on-brand">{t("actions.create")}</span>
          <span class="min-w-0 flex-1" />
          <span class="inline-flex shrink-0 items-center gap-1.5 text-sm text-brand">
            <TbInfoCircle class="size-4" aria-hidden="true" />
            {t("action.learn_more")}
          </span>
        </div>
        <p class="mb-2 text-sm text-secondary-text">{t("actions.page.intro")}</p>
      </div>

      <div class="mb-4 px-4">
        <span class="block w-full rounded-lg border border-primary-border bg-tile px-3 py-2 text-sm text-muted-text">
          {t("actions.search_placeholder")}
        </span>
      </div>

      <div class="mb-4 flex items-center justify-end gap-2 px-4">
        <span class="flex items-center gap-1 rounded-md border border-primary-border p-0.5">
          <TbLayoutList class="size-6 rounded p-1 text-secondary-text" aria-hidden="true" />
          <TbLayoutGrid class="size-6 rounded bg-brand p-1 text-on-brand" aria-hidden="true" />
        </span>
        <span class="text-sm whitespace-nowrap text-secondary-text">{t("label.sort_by")}</span>
        <span class={`${BUTTON} bg-panel text-sm text-primary-text`}>{t("label.most_recent")}</span>
      </div>

      <div class="grid grid-cols-2 gap-4 px-4">
        <For each={props.data.actions}>
          {(action, i) => (
            <ActionCard action={action} owner={props.data.user.name} ran={i() === 0 ? props.data.ran : undefined} />
          )}
        </For>
      </div>
    </div>
  );
};

/**
 * The schedule badge a card wears: green with a calendar for a schedule, blue
 * with a pulse for a trigger, amber once paused. Its text is the app's own,
 * which is English in every language.
 */
const StatusBadge: Component<{ action: Action; class?: string }> = (props) => {
  const { t } = useI18n();
  const text = () => {
    if (props.action.paused) return t("actions.paused");
    if (props.action.cron) return formatCronSchedule(props.action.cron);
    return formatTriggerKind(props.action.trigger ?? "");
  };

  return (
    <span
      class={`inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-xs ${props.class ?? ""}`}
      classList={{
        "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300": props.action.paused,
        "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300":
          !props.action.paused && !!props.action.cron,
        "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300": !props.action.paused && !props.action.cron,
      }}
    >
      <Show
        when={props.action.paused}
        fallback={
          <Show when={props.action.cron} fallback={<TbActivity class="size-3" aria-hidden="true" />}>
            <TbCalendarEvent class="size-3" aria-hidden="true" />
          </Show>
        }
      >
        <TbPlayerPause class="size-3" aria-hidden="true" />
      </Show>
      <span class="max-w-37.5 truncate">{text()}</span>
    </span>
  );
};

const SlackDestination: Component<{ channel: string }> = (props) => {
  const { t } = useI18n();
  return (
    <p class="flex min-w-0 items-center gap-1.5 text-xs text-muted-text">
      <Dynamic component={INTEGRATION_PROVIDER_ICONS.slack} class="size-3.5 shrink-0" />
      <span class="truncate">{t("actions.slack_destination", { channel: props.channel })}</span>
    </p>
  );
};

/**
 * An action card in grid view, as it sits when not hovered. `ran` is a run that
 * lands on top of its recent runs mid-scene; the list keeps three rows' height,
 * so the oldest slides out of view as it arrives.
 */
const ActionCard: Component<{ action: Action; owner: string; ran?: Run }> = (props) => {
  const { t, currentLanguage } = useI18n();
  const runs = () => props.action.runs.slice(0, 3);

  return (
    <div class="relative flex h-full min-w-0 flex-col gap-2 overflow-hidden rounded-xl border-2 border-primary-border bg-panel p-3">
      <span class="min-w-0 truncate text-sm font-medium text-primary-text">{props.action.name}</span>
      <p class="mt-0.5 line-clamp-2 text-xs text-muted-text">{props.action.description}</p>
      <div class="min-w-0">
        <p class="text-xs text-muted-text">
          {t("label.created")} {relative(props.action.hoursAgo, currentLanguage())}
        </p>
        <p class="truncate text-xs text-muted-text">{t("actions.owner_label", { name: props.owner })}</p>
        <Show when={props.action.slack}>{(channel) => <SlackDestination channel={channel()} />}</Show>
      </div>
      <div class="mt-auto flex items-center pt-1">
        <StatusBadge action={props.action} />
      </div>
      <div class="mt-1 border-t border-primary-border pt-2">
        <p class="mb-1.5 text-xs font-medium text-secondary-text">{t("actions.recent_runs")}</p>
        <Show
          when={runs().length > 0}
          fallback={<p class="py-1 text-xs text-muted-text">{t("actions.run_history_empty")}</p>}
        >
          <div class="flex flex-col" classList={{ "h-21 overflow-hidden": !!props.ran }}>
            <Show when={props.ran}>
              {(ran) => (
                <div class="grow-in grid grid-rows-[1fr]" style={at(T.card)}>
                  <div class="min-h-0 overflow-hidden">
                    <div class="row-slide pb-1" style={at(T.card)}>
                      <CardRun run={ran()} />
                    </div>
                  </div>
                </div>
              )}
            </Show>
            <For each={runs()}>
              {(run) => (
                <div class="pb-1">
                  <CardRun run={run} />
                </div>
              )}
            </For>
          </div>
          <span class="mt-0.5 flex w-full items-center justify-between gap-1 text-xs text-muted-text">
            {t("actions.view_all_runs")}
            <TbArrowRight class="size-3 shrink-0" aria-hidden="true" />
          </span>
        </Show>
      </div>
    </div>
  );
};

const CardRun: Component<{ run: Run }> = (props) => {
  const { t, currentLanguage } = useI18n();
  return (
    <span class="flex w-full items-center justify-between gap-2 rounded px-1.5 py-1">
      <span class="min-w-0 flex-1 truncate text-xs text-primary-text">{props.run.title}</span>
      <span class="shrink-0 text-[10px] text-muted-text">
        {relative(props.run.hoursAgo, currentLanguage())}
        {" · "}
        {t("actions.run_credits", { n: formatCredits(props.run.credits, currentLanguage()) })}
      </span>
    </span>
  );
};

/**
 * The first action's detail page. From `@3xl` it sits in the side panel beside
 * the list; narrower, it is the main view and leads with the link back to the
 * list, as it does on a phone.
 */
export const ActionDetail: Component<{ data: ActionsData }> = (props) => {
  const { t, currentLanguage } = useI18n();
  const action = () => props.data.actions[0];
  const chip = "inline-flex items-center gap-1.5 rounded-lg border border-primary-border bg-tile px-1.5 py-1 text-sm";

  return (
    <div class="relative flex h-full min-w-0 flex-col overflow-hidden bg-canvas @3xl:rounded-2xl @3xl:border @3xl:border-primary-border @3xl:bg-panel @3xl:pt-10">
      <span class="absolute top-2 left-2 hidden items-center gap-1 @3xl:flex">
        <span class={`${BUTTON} border-primary-border bg-panel shadow-lg`}>
          <TbChevronsRight class="size-4" aria-hidden="true" />
        </span>
        <span class="flex items-center justify-center rounded-lg p-1.5">
          <TbArrowsDiagonal class="size-4" aria-hidden="true" />
        </span>
      </span>

      <div class="flex flex-col p-4">
        <span class="mb-3 -ml-1 inline-flex items-center gap-1 self-start rounded-md px-2 py-1 text-sm text-secondary-text @3xl:hidden">
          <TbArrowLeft class="size-4" aria-hidden="true" />
          {t("actions.title")}
        </span>

        <div class="mb-4">
          <div class="flex min-w-0 items-center justify-between gap-2">
            <h3 class="min-w-0 truncate text-lg font-semibold text-primary-text">{action().name}</h3>
            <div class="flex shrink-0 items-center gap-2">
              <span class={`${BUTTON} text-sm`}>
                <TbPencil class="size-4" aria-hidden="true" />
                {t("common.edit")}
              </span>
              <span class={`${BUTTON} text-sm text-red-500`}>
                <TbTrash class="size-4" aria-hidden="true" />
                {t("common.delete")}
              </span>
            </div>
          </div>
          <p class="mt-1 text-sm text-secondary-text">{action().description}</p>
          <p class="mt-1 text-xs text-muted-text">{t("actions.owner_label_detail", { name: props.data.user.name })}</p>
        </div>

        <div class="mb-4 flex flex-wrap gap-3">
          <div class="min-w-full flex-1 rounded-lg border border-primary-border bg-tile p-3 @3xl:min-w-35">
            <div class="flex items-center justify-between gap-2 text-sm">
              <span class="flex min-w-0 items-center gap-2">
                <TbCalendarEvent class="size-4 shrink-0 text-emerald-600" aria-hidden="true" />
                <span class="truncate font-medium text-primary-text">{t("actions.schedule")}</span>
              </span>
              <span class={`${BUTTON} shrink-0 text-sm`}>
                <TbPlayerPause class="size-4" aria-hidden="true" />
                {t("actions.pause")}
              </span>
            </div>
            <Show when={action().cron}>
              {(cron) => (
                <>
                  <p class="mt-1 text-xs text-muted-text">{formatCronSchedule(cron())}</p>
                  <p class="text-xs text-muted-text">{cron().location}</p>
                </>
              )}
            </Show>
          </div>
          <div class="min-w-full flex-1 overflow-hidden rounded-lg border border-primary-border bg-tile p-3 @3xl:min-w-35">
            <div class="flex items-center gap-2 text-sm">
              <TbMail class="size-4 text-brand" aria-hidden="true" />
              <span class="font-medium text-primary-text">{t("actions.email_notification")}</span>
            </div>
            <ul class="mt-1.5 space-y-0.5">
              <For each={props.data.recipients}>
                {(email) => (
                  <li class="flex items-center gap-1 truncate text-xs text-emerald-600 dark:text-emerald-400">
                    <span class="size-1.5 shrink-0 rounded-full bg-emerald-500" />
                    <span class="truncate">
                      {email} : {t("actions.status_subscribed")}
                    </span>
                  </li>
                )}
              </For>
            </ul>
          </div>
        </div>

        <div class="mb-4">
          <h4 class="mb-2 text-sm font-medium text-primary-text">{t("actions.prompt")}</h4>
          <p class="rounded-lg border border-primary-border bg-canvas px-3 py-2.5 text-sm leading-relaxed text-primary-text">
            {props.data.prompt}
          </p>
        </div>

        <div class="mb-4 flex flex-wrap items-center gap-1.5 text-sm opacity-70 grayscale">
          <span class="text-xs leading-none text-muted-text">{t("prompt.config_label_using")}</span>
          <span class={`${chip} border-muted-border text-muted-text`}>
            <RiUserFacesUserStarLine class="size-3.5 shrink-0" aria-hidden="true" />
            {t("prompt.powering_chat_default_voice_pill")}
          </span>
          <span class={`${chip} text-primary-text`}>
            <TbBrain class="size-3.5 shrink-0" aria-hidden="true" />
            {t("mode_selector.expert")}
          </span>
          <span class={`${chip} text-primary-text`}>
            <TbGlobe class="size-3.5 shrink-0" aria-hidden="true" />
            {t("prompt.public_data_segment_automatic")}
          </span>
          <span class={`${chip} text-primary-text`}>
            <LuLibrary class="size-3.5 shrink-0" aria-hidden="true" />
            {t("actions.scope_all_files")}
          </span>
        </div>

        <div class="mb-2 flex items-center justify-between gap-2">
          <h4 class="text-sm font-medium text-primary-text">{t("actions.run_history")}</h4>
          <span class="relative shrink-0">
            <span class={`press ${BUTTON} border-transparent bg-brand text-sm text-on-brand`} style={at(T.press)}>
              <TbPlayerPlay class="size-4" aria-hidden="true" />
              {t("actions.run_now")}
            </span>
            <Cursor start={T.cursor} />
          </span>
        </div>

        <div class="flex flex-col">
          <div class="grow-in grid grid-rows-[1fr]" style={at(T.landed)}>
            <div class="min-h-0 overflow-hidden">
              <div class="row-slide pb-2" style={at(T.landed)}>
                <LandedRun run={props.data.ran} now={props.data.now} />
              </div>
            </div>
          </div>
          <For each={action().runs}>
            {(run) => (
              <div class="pb-2">
                <div class="flex items-center justify-between gap-3 rounded-lg border border-primary-border bg-panel p-3">
                  <span class="min-w-0 flex-1 truncate text-sm font-medium text-primary-text">{run.title}</span>
                  <span class="shrink-0 text-xs whitespace-nowrap text-muted-text">
                    {relative(run.hoursAgo, currentLanguage())}
                  </span>
                </div>
              </div>
            )}
          </For>
        </div>
      </div>
    </div>
  );
};

/**
 * The run Run Now starts, through its life in the history: untitled and
 * running, then titled, then done and hovered, which opens what it produced.
 * Each state that gives way is an exit element, so the finished frame is the
 * hovered, finished row.
 */
const LandedRun: Component<{ run: Run; now: string }> = (props) => {
  const { t, currentLanguage } = useI18n();
  const ranAt = () =>
    new Date(`${props.now}Z`).toLocaleString(currentLanguage(), {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: "UTC",
    });

  return (
    <div class="relative rounded-lg border border-primary-border bg-panel p-3">
      <span
        class="mark-in pointer-events-none absolute -inset-px rounded-lg border border-brand/30"
        style={at(T.expand)}
      />
      <div class="flex items-center justify-between gap-3">
        <h5 class="flex min-w-0 flex-1 text-sm font-medium text-primary-text">
          <span class="grid min-w-0 flex-1">
            <span class="exit col-start-1 row-start-1 truncate" style={at(T.titled)}>
              {t("actions.run_history_empty_title")}
            </span>
            <span class="mark-in col-start-1 row-start-1 truncate" style={at(T.titled)}>
              {props.run.title}
            </span>
          </span>
        </h5>
        <TbLoader2
          class="exit size-3.5 shrink-0 text-muted-text motion-safe:animate-spin"
          style={at(T.expand - 0.4)}
          aria-hidden="true"
        />
        <span class="exit shrink-0 text-xs whitespace-nowrap text-muted-text" style={at(T.expand)}>
          {relative(0, currentLanguage())}
        </span>
        <span class="relative size-0 self-center">
          <Cursor start={T.hover} />
        </span>
      </div>
      <div class="grow-in grid grid-rows-[1fr]" style={at(T.expand)}>
        <div class="min-h-0 overflow-hidden">
          <div class="mt-1.5 flex flex-col gap-0.5 text-xs text-muted-text">
            <p>
              {t("actions.run_type_label")}: {t("actions.run_type_manual")}
            </p>
            <p>
              {t("actions.run_at_label")}: {ranAt()}
            </p>
            <div class="mt-1 flex items-center gap-2">
              <span
                class={`inline-flex w-fit items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium ${assetLifecycleStatusPillClasses("READY")}`}
              >
                <TbCheck class="size-3 shrink-0" aria-hidden="true" />
                {t("asset_lifecycle_short_ready")}
              </span>
              <span class="text-xs text-brand">{t("actions.view_file")}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
